import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { clamp } from 'utils/math';
import CarouselChevronLeft from 'components/icons/carouselChevronLeft';
import CarouselChevronRight from 'components/icons/carouselChevronRight';

import s from 'components/editor/pages/landing/scrollList/scrollList.sss';

export default class ScrollList extends Component {

	constructor(props) {
		super(props);

		this.state = {
			canScrollLeft: false,
			canScrollRight: false,
		};
		this.scrollbarSize = 0;
		this.scrollProgress = 0;

		this.scrollerRef = this.scrollerRef.bind(this);
		this.progressRef = this.progressRef.bind(this);
		this.onProgressPointerDown = this.onProgressPointerDown.bind(this);
		this.onPointerMove = this.onPointerMove.bind(this);
		this.onPointerUp = this.onPointerUp.bind(this);
		this.onScroll = this.onScroll.bind(this);
		this.onResize = this.onResize.bind(this);
		this.scrollLeft = this.scrollBy.bind(this, -1);
		this.scrollRight = this.scrollBy.bind(this, 1);
		this.renderItem = this.renderItem.bind(this);
	}

	componentDidMount() {
		this.resizeObserver = new ResizeObserver(this.onResize);
		this.resizeObserver.observe(this.$scroller);
		document.addEventListener('pointermove', this.onPointerMove);
		document.addEventListener('pointerup', this.onPointerUp);
	}

	componentWillUnmount() {
		this.resizeObserver?.disconnect();
		document.removeEventListener('pointermove', this.onPointerMove);
		document.removeEventListener('pointerup', this.onPointerUp);
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.itemsHaveChanged(prevProps, prevState)) {
			// If the content has changed, there is a chance that the scroll size/position has changed
			this.checkScrollPosition();
		}
	}

	get items() {
		return this.props.items || [];
	}
	itemsHaveChanged(prevProps, prevState) {
		// Might not be the best way as it forces implementers to update this if they change the way items are provided
		// There could be a more generic/automated way (e.g. save the "previous" list after each render)
		return this.items !== prevProps?.items;
	}

	scrollerRef(e) {
		this.$scroller = e;
	}
	progressRef(e) {
		this.$progress = e;
	}

	onProgressPointerDown(e) {
		if (e.pointerType === 'mouse' && e.button !== 0) {
			return;
		}
		if (this.progressGrabbed || !this.scrollbarSize) {
			return;
		}
		e.preventDefault();
		const rect = this.$progress.getBoundingClientRect();
		let trackSize = rect.width;
		let thumbSize = this.scrollbarSize * trackSize;
		let thumbStart = this.scrollProgress * (trackSize - thumbSize);
		let at = e.clientX - rect.left;
		// Click outside thumb: jump towards the click (similar to native scrollbars behaviour)
		if (at < thumbStart) {
			return this.scrollBy(-1);
		}
		if (at > thumbStart + thumbSize) {
			return this.scrollBy(1);
		}
		clearTimeout(this.reenableSnapTimer);
		this.base.classList.add(s.grabbingScrollbar);
		this.base.classList.add(s.scrollbarScrolling);
		this.progressGrabbed = { thumbPosition: (at - thumbStart) / thumbSize };
	}

	onPointerMove(e) {
		if (!this.progressGrabbed) return;
		e.preventDefault();
		const rect = this.$progress.getBoundingClientRect();
		let x = e.clientX - rect.left;
		// Recalculate the thumb size in case it has changed during the drag
		let trackSize = rect.width;
		let thumbSize = this.scrollbarSize * trackSize;
		let newThumbStart = x - thumbSize * this.progressGrabbed.thumbPosition;
		let newScrollProgress = newThumbStart / (trackSize - thumbSize);
		this.$scroller.scrollLeft = newScrollProgress * (this.$scroller.scrollWidth - this.$scroller.clientWidth);
	}

	onPointerUp(e) {
		if (!this.progressGrabbed) return;
		e.stopPropagation();
		this.progressGrabbed = null;
		this.disableSnap = true;
		// Move smoothly to the nearest snap-point
		this.scrollBy(0);
		this.base.classList.remove(s.grabbingScrollbar);
		// Sadly just enabling causes an abrupt jump in scroll position, so we have to delay it
		this.reenableSnapTimer = setTimeout(() => {
			this.base.classList.remove(s.scrollbarScrolling);
			this.disableSnap = false;
		}, 250);
	}

	onScroll() {
		this.checkScrollPosition();
	}

	onResize() {
		// Check the scroll position as the size of the container might have changed
		this.checkScrollPosition();
	}

	checkScrollPosition() {
		if (!this.$scroller) return;

		const scroll = this.$scroller.scrollLeft;
		const scrollWidth = this.$scroller.scrollWidth;
		const clientWidth = this.$scroller.clientWidth;
		const maxScroll = scrollWidth - clientWidth;
		const canScrollLeft = scroll > 2;
		const canScrollRight = scroll < maxScroll - 2;
		if (canScrollLeft !== this.state.canScrollLeft) {
			this.setState({ canScrollLeft });
		}
		if (canScrollRight !== this.state.canScrollRight) {
			this.setState({ canScrollRight });
		}
		// Avoid a full rerender for every scroll event: instead of using the state, we set the property directly (+ save the value for potential future renders)
		this.scrollProgress = maxScroll ? scroll / maxScroll : 0;
		this.$scroller.parentElement.style.setProperty('--scroll-progress', this.scrollProgress);
		this.scrollbarSize = scrollWidth <= clientWidth ? 0 : clientWidth / scrollWidth;
		this.$scroller.parentElement.style.setProperty('--scrollbar-size', this.scrollbarSize);
	}

	scrollList(destination) {
		if (!this.$scroller) {
			return;
		}
		const scrollPadding = Math.round(parseFloat(getComputedStyle(this.$scroller).paddingLeft));
		const currentScroll = this.temporaryScrollDestination ?? this.$scroller.scrollLeft;
		if (typeof destination === 'function') {
			destination = destination.call(this, {
				currentScroll,
				scrollPadding,
			});
		}
		let newScroll = null;
		if (typeof destination === 'number') newScroll = destination;
		if (destination instanceof Element) newScroll = destination.offsetLeft - scrollPadding;
		if (newScroll != null && !isNaN(newScroll) && newScroll !== currentScroll) {
			this.setTemporaryScrollDestination(newScroll);
			this.$scroller.scrollTo({ left: newScroll, behavior: 'smooth' });
		}
	}
	scrollBy(amount = 1) {
		this.scrollList(({ currentScroll, scrollPadding }) => {
			const options = [...this.$scroller.children];
			if (!amount) {
				// If we're scrolled all the way to the right (or close to it), we want it to stay stuck there
				const max = this.$scroller.scrollWidth - this.$scroller.clientWidth;
				if (currentScroll >= max - 8) {
					// return options[options.length - 1];
					return max;
				}

				// Slight possible optimization: instead of full map, loop through and stop once the numbers start to get bigger
				return options.map(el => ({ el, dist: Math.abs(el.offsetLeft - currentScroll - scrollPadding) })).sort((a, b) => a.dist - b.dist)[0]?.el;
			}
			const distances = options.map(el => ({ el, dist: (el.offsetLeft - currentScroll - scrollPadding) * Math.sign(amount) })).filter(e => e.dist > 4);
			const resultIndex = clamp(Math.abs(amount) - 1, 0, distances.length - 1);
			return distances.sort((a, b) => a.dist - b.dist)[resultIndex]?.el;
		});
	}

	setTemporaryScrollDestination(position) {
		// For better UX when spamming the scroll arrows, the end position of the current scroll movement (if any) should be assumed
		// But there is no way to know when scrollTo({ behaviour: 'smooth' }) ends, nor to differentiate user-initiated scroll events from programmatic ones
		// So just keep it for a short time - it might not match the actual scroll duration precisely but that should be fine
		this.temporaryScrollDestination = position;
		clearTimeout(this.temporaryScrollDestinationTimer);
		this.temporaryScrollDestinationTimer = setTimeout(() => this.temporaryScrollDestination = null, 250);
	}

	renderTitle() {}

	renderList(items) {
		return (
			<div class={s.list}>
				{this.renderNavigation()}
				<div
					class={joinClasses(s.scroller, this.s?.scroller)}
					style={`--scroll-progress: ${this.scrollProgress}; --scrollbar-size: ${this.scrollbarSize};`}
					ref={this.scrollerRef}
					onScroll={this.onScroll}
				>
					{items.map((item, i) => this.renderItem(item, i))}
				</div>
			</div>
		);
	}

	renderNavigation() {
		return (
			<div class={joinClasses(s.navigation, this.s?.navigation)} data-section="#">
				<div class={s.progress} onPointerDown={this.onProgressPointerDown} ref={this.progressRef} />
				<div class={s.controls}>
					<button class={joinClasses(s.arrow, this.s?.arrow)} onClick={this.scrollLeft} disabled={!this.state.canScrollLeft}><CarouselChevronLeft /></button>
					<button class={joinClasses(s.arrow, this.s?.arrow)} onClick={this.scrollRight} disabled={!this.state.canScrollRight}><CarouselChevronRight /></button>
				</div>
			</div>
		);
	}

	renderItem() {}

	render(props) {
		return (
			<div id={props.id} class={joinClasses(s.wrapper, this.s?.wrapper, this.progressGrabbed && s.grabbingScrollbar, (this.progressGrabbed || this.disableSnap) && s.scrollbarScrolling)}>
				{this.renderTitle()}
				{this.renderList(this.items)}
			</div>
		);
	}
}